/* Copyright (c) 2020-present CloudIO Inc */

import { getCustomChildItemTypes } from '../../registry/Manager';
import { page as SamplePage } from '../../help/sample_pages/SplitPanelPage';
import { preventIfParentPathContainsAutoLayout } from '../../utils/ManagerUtils';
import { Platform } from '../../platform/Platform';
import {
  ComponentManager,
  ComponentManagerFn,
  Item,
} from '../../registry/Component.type';
import { ItemType } from '../../registry/ComponentRegistry';
import { baseComponentManager } from '../../registry/Components';
import { verticalSplitFixedSide } from '../../registry/constants';
import { defaultValue } from './VerticalSplitPanel';
import { splitPanelChildTypes } from '../HorizontalSplitPanel/HorizontalSplitPanelManager';

const MIN_HEIGHT = 40;
const MAX_HEIGHT = 1800;
const name = 'Vertical Split Panel';

const VerticalSplitPanelManager: ComponentManagerFn<'VerticalSplitPanel'> = (
  platform: Platform,
): ComponentManager<'VerticalSplitPanel'> => {
  return {
    ...baseComponentManager<'VerticalSplitPanel'>(
      platform,
      'VerticalSplitPanel',
    ),
    defaultValue,
    isChildAllowed: (page, item, childType) =>
      (
        [
          ...getCustomChildItemTypes('VerticalSplitPanel'),
          ...splitPanelChildTypes,
        ] as ItemType[]
      ).includes(childType) &&
      (item.children.length < 2 ? true : `${name} can only have two children!`),
    isAllowedInsideParent: (page, item) =>
      preventIfParentPathContainsAutoLayout(page, item, name),
    props: {
      fixedSide: {
        label: 'Fixed Side',
        type: 'select',
        options: verticalSplitFixedSide,
        required: true,
        category: 'Layout',
      },
      fixedWidthType: {
        label: 'Width Type',
        type: 'select',
        options: { px: 'Pixel', '%': 'Percent' },
        category: 'Layout',
      },
      fixedWidthPercent: {
        label: 'Width %',
        type: 'number',
        max: 95,
        min: 5,
        step: 5,
        category: 'Layout',
      },
      fixedWidth: {
        label: 'Width',
        type: 'number',
        max: MAX_HEIGHT,
        min: MIN_HEIGHT,
        step: 40,
        category: 'Layout',
      },
      fixedWidthMin: {
        label: 'Min Width',
        type: 'number',
        max: MAX_HEIGHT,
        min: MIN_HEIGHT,
        step: 40,
        category: 'Layout',
      },
      fixedWidthMax: {
        label: 'Max Width',
        type: 'number',
        max: MAX_HEIGHT,
        min: MIN_HEIGHT,
        step: 40,
        category: 'Layout',
      },
      padding: {
        label: 'Padding',
        type: 'padding',
        category: 'Spacing',
      },
      border: {
        label: 'Border',
        type: 'border',
        category: 'Spacing',
      },
    },
    isPropertyVisible: (property, itemData) => {
      switch (property) {
        case 'fixedWidthPercent':
          return itemData.fixedWidthType === '%';
        case 'fixedWidth':
          return itemData.fixedWidthType === 'px';
        default:
          return true;
      }
    },
    onReplace: (
      item: Item<'VerticalSplitPanel'>,
      fromItem: Item<'HorizontalSplitPanel'> | Item<'FlexPanel'>,
      page,
    ) => {
      if (fromItem.type === 'FlexPanel') {
        const children: string[] = [];
        fromItem.children.forEach((childId, index) => {
          const child = page[childId] as Item<'FlexItem'>;
          if (child.type === 'FlexItem') {
            if (child.props.sizeType === 'minmax' && child.props.max) {
              item.props.fixedWidth = child.props.max;
              item.props.fixedSide = index === 0 ? 'start' : 'end';
            }
            child.children.forEach((c) => {
              children.push(c);
              page[c] = { ...page[c], parent: item.itemId };
            });
          }
          delete page[childId];
        });
        item.children = children;
      } else if (fromItem.type === 'HorizontalSplitPanel') {
        item.props.fixedWidthType = fromItem.props.fixedHeightType;
        item.props.fixedWidth = fromItem.props.fixedHeight;
        item.props.fixedWidthPercent = fromItem.props.fixedHeightPercent;
        item.props.fixedWidthMin = fromItem.props.fixedHeightMin;
        item.props.fixedWidthMax = fromItem.props.fixedHeightMax;
      }
      return item;
    },
    category: 'Containers',
    name,
    canHaveChildren: () => true,
    layout: 'fixed',
    icon: { iconName: 'border-center-v' },
    replaceableWith: ['HorizontalSplitPanel', 'FlexPanel'],
    description:
      'Vertical Split Panel creates a panel that arrange sections into a single vertical column and allows you to interactively change/resize the proportion of the width dedicated to each of the section.',
    helpRenders: [
      {
        title: 'Vertical Split Panel',
        description: `Adjust the various width props and see how it behaves. 

\`\`\`alert
Note: A minimum of 40px width will be maintained on either side irrespective of the min & max width configuration.
\`\`\`
`,
        page: SamplePage,
        exampleId: 'e1',
        height: 400,
        visibleProps: [
          'fixedWidthType',
          'fixedWidthPercent',
          'fixedWidth',
          'fixedWidthMax',
          'fixedWidthMin',
          'fixedSide',
        ],
        item: {
          itemId: 'horizontalSplitPanel',
          props: {
            fixedWidth: 300,
            fixedWidthMax: 1800,
            fixedWidthMin: 40,
          },
          children: ['paper1', 'paper'],
          type: 'VerticalSplitPanel',
          parent: 'root',
        },
      },
    ],
  };
};

export default VerticalSplitPanelManager;
